import React from "react"
import { Image, TouchableOpacity, View } from "react-native"
import { Body, Small } from "@/components/standard/BFText"
import { Horizontal, Vertical } from "@/components/standard/BFLayout"
import { Card, CardContent } from "@/components/reusables/ui/card"
import { Badge } from "@/components/reusables/ui/badge"
import { distance, formatShortDate, parseCoordinate } from "@/utils/NumberUtils"
import CheckIcon from "phosphor-react-native/src/icons/Check"
import StarIcon from "phosphor-react-native/src/icons/Star"

type CompCategory =
  | "selected"
  | "closest"
  | "lowest"
  | "highest"
  | "ownScenario"

interface CompCardProps {
  comp: any
  analysis?: any
  onPress?: (compId: string) => void
  onImagePress?: (comp: any) => void
  categories?: CompCategory[]
  onToggleOwnScenario?: (comp: any) => void
}

export function CompCard({
  comp,
  analysis,
  onPress,
  onImagePress,
  categories = [],
  onToggleOwnScenario,
}: CompCardProps) {
  if (!comp?.arvcomp) {
    return null
  }

  const { arvcomp } = comp
  const isSelected = comp.isSelected
  const subjectProperty = analysis?.property

  const formatCurrency = (value?: number | null) => {
    if (!value) return "—"
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value)
  }

  const getDistance = () => {
    if (!subjectProperty?.latitude || !subjectProperty?.longitude) return null
    if (!arvcomp.latitude || !arvcomp.longitude) return null

    return distance(
      parseCoordinate(subjectProperty.latitude as string | number) ?? 0,
      parseCoordinate(subjectProperty.longitude as string | number) ?? 0,
      parseCoordinate(arvcomp.latitude as string | number) ?? 0,
      parseCoordinate(arvcomp.longitude as string | number) ?? 0,
      "MILES"
    )
  }

  const distanceValue = getDistance()

  const getCategoryBadgeInfo = (category: CompCategory) => {
    switch (category) {
      case "selected":
        return {
          label: "Selected",
          variant: "default" as const,
          icon: <CheckIcon size={12} color="white" />,
        }
      case "closest":
        return {
          label: "3 Closest",
          variant: "secondary" as const,
        }
      case "lowest":
        return {
          label: "3 Lowest $/sq ft",
          variant: "outline" as const,
        }
      case "highest":
        return {
          label: "3 Highest $/sq ft",
          variant: "warning" as const,
        }
      case "ownScenario":
        return {
          label: "Own Scenario",
          variant: "destructive" as const,
          icon: <StarIcon size={12} color="white" />,
        }
    }
  }

  const isInOwnScenario = categories.includes("ownScenario")

  const handlePress = () => {
    if (onToggleOwnScenario) {
      onToggleOwnScenario(comp)
    } else {
      onPress?.(comp.id as string)
    }
  }

  return (
    <Card
      className={`border ${isSelected ? "border-primary bg-primary/5" : "border-border"} ${
        onToggleOwnScenario ? "shadow-sm" : ""
      }`}
    >
      <CardContent className="p-0">
        <Horizontal className="gap-0">
          {/* Property Image */}
          <TouchableOpacity
            testID={`comp-image-${comp.id}`}
            onPress={() => onImagePress?.(comp)}
            className="size-20 rounded-l-md overflow-hidden bg-secondary"
          >
            {arvcomp.heroImage ? (
              <Image
                source={{ uri: arvcomp.heroImage }}
                className="size-full"
                resizeMode="cover"
              />
            ) : (
              <View className="size-full bg-secondary flex items-center justify-center">
                <Small className="text-muted-foreground">No Image</Small>
              </View>
            )}
          </TouchableOpacity>

          {/* Property Details - Clickable Area */}
          <TouchableOpacity
            testID={`comp-card-${comp.id}`}
            onPress={handlePress}
            activeOpacity={0.8}
            className="flex-1 group"
          >
            <Vertical className="flex-1 gap-1 p-2 relative">
              {/* Hover Text Overlay */}
              {onToggleOwnScenario && (
                <View
                  className="absolute inset-0 bg-primary/20 rounded-r-md flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                  style={{ pointerEvents: "none" }}
                >
                  <View className="bg-white/95 px-3 py-2 rounded-md border border-primary/20 shadow-sm">
                    <Small className="text-primary font-bold text-center">
                      {isInOwnScenario
                        ? "Remove from\nOwn Scenario"
                        : "Add to\nOwn Scenario"}
                    </Small>
                  </View>
                </View>
              )}
              <Horizontal className="items-start justify-between">
                <Body
                  className="font-medium text-foreground flex-1"
                  numberOfLines={2}
                >
                  {arvcomp.fullAddress || "Address not available"}
                </Body>

                {/* Category Badges and Toggle Indicator */}
                <Horizontal className="gap-1 ml-2 flex-wrap items-center">
                  {categories.length > 0 && (
                    <Horizontal className="gap-1 flex-wrap">
                      {categories.map((category, index) => {
                        const badgeInfo = getCategoryBadgeInfo(category)
                        return (
                          <Badge key={index} variant={badgeInfo.variant}>
                            {badgeInfo.icon && badgeInfo.icon}
                            <Small
                              className={`text-xs ${badgeInfo.icon ? "ml-1" : ""}`}
                            >
                              {badgeInfo.label}
                            </Small>
                          </Badge>
                        )
                      })}
                    </Horizontal>
                  )}

                  {/* Small toggle indicator */}
                  {onToggleOwnScenario && (
                    <View className="p-1 group-hover:opacity-0 transition-opacity duration-200">
                      <StarIcon
                        size={12}
                        color={isInOwnScenario ? "#e14a3e" : "#D9D9D9"}
                      />
                    </View>
                  )}
                </Horizontal>
              </Horizontal>

              {/* Property Stats */}
              <Horizontal className="gap-4">
                {arvcomp.bedrooms && (
                  <Small className="text-muted-foreground">
                    {arvcomp.bedrooms as number} bed
                  </Small>
                )}
                {arvcomp.bathrooms && (
                  <Small className="text-muted-foreground">
                    {arvcomp.bathrooms as number} bath
                  </Small>
                )}
                {arvcomp.area && (
                  <Small className="text-muted-foreground">
                    {(arvcomp.area as number).toLocaleString()} sqft
                  </Small>
                )}
              </Horizontal>

              {/* Price and Distance */}
              <Horizontal className="items-center justify-between">
                <Body className="font-semibold text-foreground">
                  {(() => {
                    if (arvcomp.pricing) {
                      try {
                        const pricing = JSON.parse(arvcomp.pricing as string)
                        return formatCurrency(pricing.price as number)
                      } catch {
                        return "—"
                      }
                    }
                    return "—"
                  })()}
                </Body>

                <Horizontal className="gap-2">
                  {distanceValue && (
                    <Small className="text-muted-foreground">
                      {distanceValue.toFixed(1)} mi
                    </Small>
                  )}
                  {arvcomp.dateSold && (
                    <Small className="text-muted-foreground">
                      {formatShortDate(arvcomp.dateSold as string)}
                    </Small>
                  )}
                </Horizontal>
              </Horizontal>
            </Vertical>
          </TouchableOpacity>
        </Horizontal>
      </CardContent>
    </Card>
  )
}
